/**
 * Create Your Own Story — user-authored storyboard builder.
 *
 * The user fills in:
 *   - Title           (becomes the storyboard's `video_name`)
 *   - Story           (free-form description: plot beats, moral, vibe)
 *   - Channel         (an existing Active Channel — drives the visual
 *                      style prefix that gpt-image-1 anchors to)
 *   - Cast            (zero-or-more characters from the Character
 *                      Library; their descriptions + reference photos
 *                      are auto-injected on every scene render)
 *   - Scene count     (6-12)
 *
 * Submitting calls `POST /api/storyboards/custom-story`, which uses
 * GPT-5.2 to break the story into scenes (description, dialog, audio,
 * camera, lighting, image_prompt). On success the user is bounced
 * into the standard `/storyboards/:id` editor where each scene's
 * Generate Image / Generate Preview buttons render with the cast
 * baked in.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  createCustomStoryboard,
  listActiveChannels,
} from "@/lib/api";
import {
  listCharacters,
  characterImageUrl,
} from "@/lib/charactersApi";
import { ScenePreview } from "@/components/ScenePreview";

function ChannelPicker({ channels, value, onChange, loading }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
        Channel <span className="text-zinc-700">(visual style)</span>
      </label>
      {loading ? (
        <div className="text-xs text-zinc-600 font-mono">[recon] loading channels<span className="blink">_</span></div>
      ) : channels.length === 0 ? (
        <div
          data-testid="create-story-channels-empty"
          className="border border-dashed border-zinc-800 rounded-sm py-4 px-3 text-xs text-zinc-500"
        >
          You have no Active Channels yet. The story will still render,
          but without a channel-wide visual style anchor — pick or
          create one in <strong className="text-yellow-400">Phase 4 · Active channels</strong> for
          best results.
        </div>
      ) : (
        <select
          data-testid="create-story-channel-select"
          value={value || ""}
          onChange={(e) => onChange(e.target.value || null)}
          className="w-full bg-black border border-zinc-700 text-white px-3 py-2 rounded-sm focus:border-yellow-400 outline-none font-mono text-sm"
        >
          <option value="">— No channel (skip visual anchor) —</option>
          {channels.map((c) => (
            <option key={c.channel_id} value={c.channel_id}>
              {c.name} {c.handle ? `· @${c.handle}` : ""}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

function CharacterPicker({ characters, selected, onToggle, loading }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
        Cast{" "}
        <span className="text-zinc-700">
          (recurring characters used in this story)
        </span>
      </label>
      {loading ? (
        <div className="text-xs text-zinc-600 font-mono">[recon] loading cast<span className="blink">_</span></div>
      ) : characters.length === 0 ? (
        <div
          data-testid="create-story-cast-empty"
          className="border border-dashed border-zinc-800 rounded-sm py-4 px-3 text-xs text-zinc-500"
        >
          You have no characters yet. Build a reusable cast in the{" "}
          <strong className="text-yellow-400">Character library</strong> page,
          then come back to write your first story.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[380px] overflow-y-auto pr-1">
          {characters.map((c) => {
            const isSelected = selected.includes(c.character_id);
            const cover = c.images?.[0];
            return (
              <button
                type="button"
                key={c.character_id}
                onClick={() => onToggle(c.character_id)}
                data-testid={`create-story-cast-toggle-${c.character_id}`}
                className={`flex items-center gap-3 p-2 border rounded-sm transition-colors text-left ${
                  isSelected
                    ? "border-yellow-400 bg-yellow-400/10"
                    : "border-zinc-800 hover:border-zinc-600"
                }`}
              >
                <div className="h-10 w-10 shrink-0 rounded-sm overflow-hidden bg-zinc-900 border border-zinc-800">
                  {cover ? (
                    <img
                      src={characterImageUrl(c.character_id, cover.image_id)}
                      alt={c.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-yellow-400 font-bold text-lg">
                      {c.name?.[0]?.toUpperCase() || "?"}
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-white text-sm font-bold truncate">{c.name}</div>
                  {c.role && (
                    <div className="font-mono text-[10px] uppercase tracking-wider text-yellow-400/80">
                      {c.role}
                    </div>
                  )}
                  {c.description && (
                    <div className="text-[11px] text-zinc-500 truncate">
                      {c.description}
                    </div>
                  )}
                </div>
                {isSelected && (
                  <span className="text-yellow-400 font-mono text-xs shrink-0">✓</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function CreateStory() {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [story, setStory] = useState("");
  const [channelId, setChannelId] = useState(null);
  // `sceneCount` may be either an integer (6-24) or the sentinel
  // string "auto". Default to "auto" so first-time users get a
  // sensible count sized to the length of their story.
  const [sceneCount, setSceneCount] = useState("auto");
  const [selectedCast, setSelectedCast] = useState([]);
  const [channels, setChannels] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [chs, cast] = await Promise.all([
          listActiveChannels().catch(() => []),
          listCharacters().catch(() => []),
        ]);
        if (cancelled) return;
        setChannels(chs);
        setCharacters(cast);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleCast = (id) =>
    setSelectedCast((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const selectedChannel = useMemo(
    () => channels.find((c) => c.channel_id === channelId) || null,
    [channels, channelId],
  );

  const canSubmit =
    title.trim().length > 0 && story.trim().length >= 20 && !busy;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) {
      if (story.trim().length < 20) {
        toast.error("Story description must be at least 20 characters");
      } else {
        toast.error("Title and story are required");
      }
      return;
    }
    setBusy(true);
    try {
      const res = await createCustomStoryboard({
        title: title.trim(),
        story: story.trim(),
        channel_id: channelId,
        channel_handle: selectedChannel?.handle || null,
        character_ids: selectedCast,
        // Pass `"auto"` through to the backend when the user picked
        // Auto — the service infers the count from the story length.
        // Otherwise send the picked integer.
        target_scenes: sceneCount === "auto" ? "auto" : parseInt(sceneCount, 10),
      });
      const chosen = res.target_scenes || res.scenes?.length || 0;
      const suffix =
        sceneCount === "auto" && chosen
          ? ` (Auto picked ${chosen})`
          : "";
      toast.success(
        `Story built — ${res.scenes?.length || 0} scenes ready to render${suffix}`,
      );
      navigate(`/storyboards/${res.storyboard_id}`);
    } catch (err) {
      // Distinguish a client-side axios timeout from a real backend
      // error — the two need very different UX copy. `err.code`
      // being `ECONNABORTED` (or a message including "timeout") is
      // axios's signal that the request hung too long client-side.
      const isTimeout =
        err?.code === "ECONNABORTED" ||
        String(err?.message || "").toLowerCase().includes("timeout");
      const detail = err?.response?.data?.detail || "Couldn't build the story";
      let friendly;
      if (isTimeout) {
        friendly =
          "This story is taking longer than 3 minutes to build. It may still finish in the background — check the Storyboards page in a minute, or try again with a shorter description or fewer scenes.";
      } else if (detail === "story_too_short") {
        friendly = "Story description must be at least 20 characters";
      } else if (detail === "title_required") {
        friendly = "Title is required";
      } else if (detail === "no_valid_scenes" || detail === "model_returned_no_scenes") {
        friendly =
          "GPT-5.2 returned no usable scenes — try a more descriptive story or lower the scene count.";
      } else if (detail === "no_openai_key") {
        friendly = "GPT-5.2 isn't configured. Add your OpenAI key in settings.";
      } else {
        friendly = String(detail);
      }
      toast.error(friendly);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="create-story-page"
      className="px-6 lg:px-10 py-8 max-w-[1100px] mx-auto"
    >
      <div className="mb-6">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-yellow-400 mb-2">
          phase 3 · create content · your own story
        </div>
        <h1 className="text-white text-4xl font-black mb-2">Create your own story</h1>
        <p className="text-zinc-400 text-sm max-w-2xl">
          Write a short story description and pick your cast. On{" "}
          <em>Auto</em>, our AI reads your story and picks the natural
          number of scenes — one per beat, location shift, or major
          action. Want manual control? Drop{" "}
          <code className="text-yellow-400 bg-zinc-900 px-1 rounded">
            &lt;scene02&gt;
          </code>{" "}
          tags anywhere in the description to force scene breaks at
          exactly those spots.
        </p>
      </div>

      <form
        onSubmit={submit}
        data-testid="create-story-form"
        className="space-y-6 border border-zinc-800 rounded-sm bg-zinc-950/40 p-6"
      >
        <div>
          <label
            htmlFor="story-title"
            className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2"
          >
            Story title *
          </label>
          <input
            id="story-title"
            data-testid="create-story-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Bella and Benny build a treehouse"
            maxLength={200}
            required
            className="w-full bg-black border border-zinc-700 text-white text-base px-3 py-2.5 rounded-sm focus:border-yellow-400 outline-none"
          />
        </div>

        <div>
          <label
            htmlFor="story-text"
            className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2"
          >
            Story description *{" "}
            <span className="text-zinc-700 normal-case tracking-normal">
              ({story.trim().length}/6000 chars, ≥ 20 required)
            </span>
          </label>
          <textarea
            id="story-text"
            data-testid="create-story-description"
            value={story}
            onChange={(e) => setStory(e.target.value.slice(0, 6000))}
            rows={8}
            placeholder="Describe what happens. Plot beats, the moral, the vibe — anything you'd tell a screenwriter friend.&#10;&#10;Example: Bella finds an old map in her backyard. She rushes inside to show Benny, who insists they need a snack first. They follow the map past the swing set, under the apple tree, and find a buried chest containing… a handwritten note from their grandma.&#10;&#10;Tip: drop <scene02>, <scene03>, … tags anywhere to force exact scene breaks. Otherwise the AI picks the breaks itself."
            className="w-full bg-black border border-zinc-700 text-zinc-200 px-3 py-2 rounded-sm focus:border-yellow-400 outline-none text-sm font-mono leading-relaxed"
            required
          />
          <ScenePreview story={story} sceneCountSetting={sceneCount} />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <ChannelPicker
            channels={channels}
            value={channelId}
            onChange={setChannelId}
            loading={loading}
          />
          <div>
            <label
              htmlFor="scene-count"
              className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2"
            >
              Scenes
            </label>
            <select
              id="scene-count"
              data-testid="create-story-scene-count"
              value={sceneCount}
              onChange={(e) => {
                const v = e.target.value;
                setSceneCount(v === "auto" ? "auto" : parseInt(v, 10));
              }}
              className="w-full bg-black border border-zinc-700 text-white px-3 py-2 rounded-sm focus:border-yellow-400 outline-none font-mono text-sm"
            >
              <option value="auto">Auto (based on story length)</option>
              {[6, 8, 10, 12, 14, 16, 18, 20, 24].map((n) => (
                <option key={n} value={n}>
                  {n} scenes
                </option>
              ))}
            </select>
            <p className="text-[11px] text-zinc-600 mt-1.5">
              Auto: AI picks the natural count (6-24) · Force a break
              anywhere by writing{" "}
              <code className="text-yellow-400/80">&lt;scene02&gt;</code>{" "}
              in the description
            </p>
          </div>
        </div>

        <CharacterPicker
          characters={characters}
          selected={selectedCast}
          onToggle={toggleCast}
          loading={loading}
        />

        <div className="flex items-center gap-3 pt-2 border-t border-zinc-900">
          <button
            type="submit"
            data-testid="create-story-submit"
            disabled={!canSubmit}
            className="bg-yellow-400 text-black font-black uppercase tracking-wide px-6 py-3 rounded-sm hover:bg-yellow-300 disabled:opacity-40 disabled:cursor-not-allowed text-sm transition-colors"
          >
            {busy ? "Building storyboard… (30-90s)" : "Create storyboard"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/storyboards")}
            disabled={busy}
            data-testid="create-story-cancel"
            className="font-mono text-xs uppercase tracking-wider px-4 py-2 border border-zinc-700 text-zinc-400 rounded-sm hover:border-zinc-500"
          >
            Cancel
          </button>
          <div className="ml-auto text-[11px] text-zinc-600 font-mono">
            {selectedCast.length} character{selectedCast.length === 1 ? "" : "s"} selected
          </div>
        </div>
      </form>
    </div>
  );
}
